import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { portfolioAPI, userRelationshipAPI } from '../../services/api';
import Avatar from '../common/Avatar';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import useBackButton from '../../hooks/useBackButton';

interface LikesListModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolioId: string;
}

interface Liker {
  id: string;
  first_name: string;
  last_name: string;
  profile_pic: string;
  profile_pic_thumb_signed?: string;
  user_type: string;
  is_following: boolean;
  deletion_scheduled_at?: string;
}

const LikesListModal: React.FC<LikesListModalProps> = ({ isOpen, onClose, portfolioId }) => {
  useBackButton(onClose, isOpen, 150);
  const [likes, setLikes] = useState<Liker[]>([]);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (isOpen && portfolioId) {
      fetchLikes();
    }
  }, [isOpen, portfolioId]);

  const fetchLikes = async () => {
    try {
      setLoading(true);
      const response = await portfolioAPI.getPortfolioLikes(portfolioId);
      if (response.data && response.data.data) {
        setLikes(response.data.data.likes);
      }
    } catch (error) {
      console.error('Failed to fetch likes', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (likerId: string, isFollowing: boolean) => {
    if (!user) return; // Prevent action if not logged in
    
    try {
      setFollowLoading(likerId);
      await userRelationshipAPI.toggleFollow(likerId);
      
      // Update local state
      setLikes(prev => prev.map(liker => 
        liker.id === likerId 
          ? { ...liker, is_following: !isFollowing }
          : liker
      ));
    } catch (error) {
      console.error('Failed to toggle follow', error);
    } finally {
      setFollowLoading(null);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="relative bg-white dark:bg-gray-900 w-full max-w-md rounded-2xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-800">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">Likes</h3>
              <button 
                onClick={onClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
              >
                <XMarkIcon className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
                </div>
              ) : likes.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No likes yet.
                </div>
              ) : (
                likes.map((liker) => (
                  <div key={liker.id} className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-3 cursor-pointer"
                      onClick={() => {
                        onClose();
                        navigate(`/profile/${liker.id}`);
                      }}
                    >
                      <Avatar 
                        src={liker.profile_pic_thumb_signed || liker.profile_pic} 
                        alt={liker.first_name} 
                        size="md"
                      />
                      <div>
                        <p className="font-semibold text-sm text-gray-900 dark:text-white capitalize">
                          {liker.first_name} {liker.last_name}
                        </p>
                        <p className="text-xs text-gray-500 capitalize">
                          {liker.user_type}
                        </p>
                      </div>
                    </div>

                    {user && user.id !== liker.id && (
                      liker.deletion_scheduled_at ? (
                        <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-semibold rounded-full">
                          Deleted
                        </span>
                      ) : (
                        <button
                          onClick={() => handleFollow(liker.id, liker.is_following)}
                          disabled={followLoading === liker.id}
                          className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all ${
                            liker.is_following
                              ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-200 dark:border-gray-700'
                              : 'bg-black dark:bg-white text-white dark:text-black hover:opacity-90'
                          }`}
                        >
                           {followLoading === liker.id ? '...' : liker.is_following ? 'Following' : 'Follow'}
                        </button>
                      )
                    )}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LikesListModal;
